/**
 * Where the ledger is, and whether it is encrypted — four states, and the moves
 * between them (Settings → Sync, `LedgerStates.vue`):
 *
 *                 readable            encrypted
 *   this device   local    ⇄ (4↔1) ⇄  sealed      (stores/sealed.js)
 *                   ⇅                   ⇅
 *   the cloud     cloud    ⇄ (2↔3) ⇄  vault       (stores/sync.js)
 *
 * A side of the square is one move: where it is kept (local ⇄ cloud, sealed ⇄
 * vault), or whether it is encrypted (local ⇄ sealed, cloud ⇄ vault). A
 * diagonal is two, through a state of this device: local → sealed → vault,
 * vault → sealed → local, cloud → local → sealed, sealed → local → cloud. The
 * second step is remembered (`okane.next`) across the reload the first ends
 * with, and Settings opens on it.
 *
 * Every move copies, checks, and only then destroys the copy it left — two live
 * copies of one ledger would drift apart. What is left to destroy is recorded
 * in `okane.move` first, so a move cut short finishes on the next start
 * (main.js). A move that shows a recovery key leaves the reload to the page,
 * after the key is written down.
 */
import { activeDriver, exportDatabase, openLocalDriver, vaultFile } from '../db'
import { copyLedger, setPendingMove } from '../db/move'
import { run } from '../db'
import {
  copyPlace,
  downloadFiles,
  fileRows,
  openFiles,
  openForCloud,
  queueUpload,
  sealedSpace,
  sealFiles,
  sealForVault,
  uploadFiles,
} from './attachments'
import { dropLater } from '../db/drops'
import { stripSync } from '../db/strip'
import * as cloud from './cloud'
import { createSealed, createSealedFromRing, unlockedRing } from './sealed'
import { addLedger, currentLedger, fileOf, removeLedger, sealedNames, setCurrentLedger, updateLedger } from './ledgers'
import { encryptInPlace, setVaultOrg, syncing, turnOn, vaultKeyring } from './sync'
import { state } from './state'

/** The state this device is in: 'local' | 'sealed' | 'cloud' | 'vault'. */
export function currentState() {
  if (cloud.getMode() !== 'cloud') return currentLedger()?.kind === 'encrypted' ? 'sealed' : 'local'
  return syncing() ? 'vault' : 'cloud'
}

const SIDES = {
  local: { cloud: 'up', sealed: 'seal' },
  sealed: { local: 'unseal', vault: 'sealedUp' },
  cloud: { local: 'down', vault: 'encrypt' },
  vault: { cloud: 'decrypt', sealed: 'sealedDown' },
}
const THROUGH = { local: { vault: 'sealed' }, vault: { local: 'sealed' }, cloud: { sealed: 'local' }, sealed: { cloud: 'local' } }

/**
 * The way from `from` to `to`: `{ steps: [move, …], through }` — one move along
 * a side, two across. `through` is the state the first step ends in.
 */
export function route(from, to) {
  if (from === to) return null
  const side = SIDES[from][to]
  if (side) return { steps: [side], through: null }
  const via = THROUGH[from][to]
  return { steps: [SIDES[from][via], SIDES[via][to]], through: via }
}

// ------------------------------------------------------------------ the second step

const NEXT_KEY = 'okane.next'
/** Where a two-step move is headed, once its first step is done — or null. */
export function nextStep() {
  try {
    return JSON.parse(localStorage.getItem(NEXT_KEY) ?? 'null')
  } catch {
    return null
  }
}
export function setNextStep(value) {
  try {
    if (value) localStorage.setItem(NEXT_KEY, JSON.stringify(value))
    else localStorage.removeItem(NEXT_KEY)
  } catch {
    // Then the second step is chosen again by hand.
  }
}

// ------------------------------------------------------------------ the moves
//
// A ledger keeps its id through every move (stores/ledgers.js): local ledgers
// are orgs, and one going up becomes the cloud org of the same id.

/** local → cloud: boot signs in (if need be) and carries the ledger up under its id (main.js, moveToCloud). */
export function up() {
  const ledger = currentLedger()
  setPendingMove({ to: 'cloud', stage: 'copy', ledger: { id: ledger.id, name: ledger.name, file: fileOf(ledger) } })
  cloud.setMode('cloud')
}

/**
 * cloud → local: the ledger's files downloaded into its place here, each
 * checked; then the ledger copied into a file of its own here, under the org's
 * id, and checked; boot deletes the org — and with it the files on the server.
 * Only an owner who shares it with nobody.
 */
export async function down({ org, onProgress }) {
  const ledger = { id: org.id, name: org.name, kind: 'plain' }
  await downloadFiles(org.id, org.id, await fileRows(), {}, onProgress)
  const local = await openLocal({ file: fileOf(ledger) })
  try {
    await copyLedger({ from: activeDriver(), to: local })
  } finally {
    local.close()
  }
  addLedger(ledger)
  setCurrentLedger(ledger.id)
  setPendingMove({ to: 'local', org: org.id })
  cloud.setOrgId(null)
  cloud.setMode('local')
  return true
}

/**
 * local → sealed: each file sealed under a key of its own (into the sealed
 * place), the keys written into the rows, then the ledger sealed behind a
 * passphrase; boot erases the readable file and the readable files. Returns
 * the recovery key.
 */
export async function seal({ passphrase, onProgress }) {
  const ledger = currentLedger()
  const keys = await sealFiles(ledger.id, await fileRows(), onProgress)
  // Into the readable ledger first: it reads its files without them, and the sealed one needs them.
  for (const [id, key] of Object.entries(keys)) await run('UPDATE attachments SET file_key = ? WHERE id = ?', [key, id])
  const recovery = await createSealed({ ...ledger, legacy: false }, await exportDatabase(), passphrase)
  dropLater(`files:${ledger.id}`)
  setPendingMove({ to: 'sealed', file: fileOf(ledger) })
  updateLedger(ledger.id, { kind: 'encrypted', file: undefined, legacy: false })
  return recovery
}

/**
 * sealed → local: each file opened into the readable place, then the ledger
 * copied into a readable file of its own and checked, its files' keys cleared;
 * boot deletes the sealed ledger and its sealed files.
 */
export async function unseal({ onProgress } = {}) {
  const ledger = currentLedger()
  const plain = { ...ledger, kind: 'plain', file: undefined, legacy: false }
  await openFiles(ledger.id, await fileRows(), onProgress)
  // The sealed ledger is in memory: the files on disk are free to open.
  const local = await openLocal({ file: fileOf(plain) })
  try {
    await copyLedger({ from: activeDriver(), to: local })
    await local.run('UPDATE attachments SET file_key = NULL')
  } finally {
    local.close()
  }
  dropLater(`files:${sealedSpace(ledger.id)}`)
  setPendingMove({ to: 'unsealed', ...sealedNames(ledger) })
  updateLedger(ledger.id, { kind: 'plain', file: undefined, legacy: false })
  return true
}

/**
 * sealed → vault: the ledger becomes the cloud org of its id (signed in), its
 * keyring the vault's — the same passphrase, the same recovery key, nothing to
 * show — its sealed files go up as they are (and stay here, kept as the org's),
 * and its copy is written into the org's file; boot deletes the sealed one. A
 * ledger encrypted before keyrings (`legacy`) gets a new one: its passphrase is
 * asked, and the recovery key returned. A move that fails half way takes the
 * new org away again, so it can be tried again.
 */
export async function sealedUp({ name, passphrase, onProgress }) {
  const ledger = currentLedger()
  const keyring = unlockedRing()
  const rows = await fileRows()
  const { org, recovery } = await turnOn({ name, passphrase, id: ledger.id, keyring })
  try {
    await uploadFiles(sealedSpace(ledger.id), org.id, rows, onProgress)
    await copyPlace(sealedSpace(ledger.id), org.id, rows)
    const file = await openLocalDriver({ file: vaultFile(org.id) })
    try {
      await file.import(await exportDatabase())
    } finally {
      file.close()
    }
  } catch (err) {
    await cloud.deleteOrg(org.id).catch(() => {})
    throw err
  }
  dropLater(`files:${sealedSpace(ledger.id)}`)
  setPendingMove({ to: 'unsealed', ...sealedNames(ledger) })
  removeLedger(ledger.id)
  setVaultOrg(org)
  cloud.setOrgId(org.id)
  cloud.setMode('cloud')
  return recovery
}

/**
 * vault → sealed: the ledger's sealed files brought here (from what this device
 * kept, else the server), each opened to check it and kept sealed; then the
 * ledger, without sync, kept encrypted on this device under the org's own
 * keyring — the same passphrase and recovery key — and its id. The org is
 * deleted by the next start, its file and kept files dropped. Only an owner who
 * shares it with nobody.
 */
export async function sealedDown({ org, onProgress }) {
  const ring = await vaultKeyring()
  const ledger = { id: org.id, name: org.name, kind: 'encrypted' }
  await downloadFiles(org.id, sealedSpace(org.id), await fileRows(), { sealed: true, cache: org.id }, onProgress)
  await createSealedFromRing(ledger, await stripSync(await exportDatabase()), ring)
  addLedger(ledger)
  setCurrentLedger(ledger.id)
  dropLater(vaultFile(org.id))
  dropLater(`files:${org.id}`)
  setPendingMove({ to: 'local', org: org.id })
  setVaultOrg(null)
  cloud.setOrgId(null)
  cloud.setMode('local')
  return null
}

/**
 * cloud → vault, in place: each file fetched and sealed under a key of its own
 * (kept here), the keys written into the vault's first snapshot, the same org
 * encrypted (stores/sync.js) — and only then the sealed files queued to
 * replace the readable ones on the server. Returns the recovery key.
 */
export async function encrypt({ org, passphrase, onProgress }) {
  const keys = await sealForVault(org.id, await fileRows(), onProgress)
  // The server closes this org's sockets — ours too — once it is encrypted.
  state.cloud.encrypting = true
  try {
    const recovery = await encryptInPlace({ org, passphrase, fileKeys: keys })
    queueUpload(org.id, Object.keys(keys))
    return recovery
  } catch (err) {
    state.cloud.encrypting = false
    throw err
  }
}

/**
 * vault → cloud, in place: each file opened here; the ledger, without sync and
 * without its files' keys, becomes the org's on the server again (the server
 * deletes the vault and tells its devices it is gone); then the opened files
 * are queued to replace the sealed ones there.
 */
export async function decrypt({ org, onProgress }) {
  const staged = await openForCloud(org.id, await fileRows(), onProgress)
  const bytes = await stripSync(await exportDatabase(), 'UPDATE attachments SET file_key = NULL')
  state.cloud.encrypting = true
  try {
    const res = await fetch(`${cloud.apiBase()}/api/orgs/${encodeURIComponent(org.id)}/decrypt`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: bytes,
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw Object.assign(new Error(body?.error?.message ?? `The server answered ${res.status}.`), { code: body?.error?.code })
    }
  } catch (err) {
    state.cloud.encrypting = false
    throw err
  }
  queueUpload(org.id, staged, `plain:${org.id}`)
  dropLater(vaultFile(org.id))
  dropLater(`files:${org.id}`)
  setVaultOrg(null)
}

async function openLocal(options) {
  try {
    return await openLocalDriver(options)
  } catch (err) {
    throw err?.code === 'db-locked' ? Object.assign(new Error(err.message), { code: 'move-locked' }) : err
  }
}
